import { useState, useEffect } from "react";
import GameLayout from "../components/GameLayout";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User, Wallet, Save, Eye, EyeOff, Link2, ShieldCheck, Mail, KeyRound, Unlink, Lock, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ProfilePage() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  // Edit Profile state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [saving, setSaving] = useState(false);

  // Link FaucetPay state
  const [fpEmail, setFpEmail] = useState("");
  const [fpPw, setFpPw] = useState("");
  const [showFpPw, setShowFpPw] = useState(false);
  const [linkingFp, setLinkingFp] = useState(false);

  // Unlink FaucetPay state
  const [unlinkPw, setUnlinkPw] = useState("");
  const [showUnlinkPw, setShowUnlinkPw] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  // Set Password state (for FP-only users)
  const [spEmail, setSpEmail] = useState("");
  const [spPassword, setSpPassword] = useState("");
  const [showSpPw, setShowSpPw] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  // Delete Account state
  const [deleteWarning, setDeleteWarning] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  const reloadProfile = async () => {
    try {
      const { data } = await API.get("/profile");
      setProfile(data);
      setName(data.user.name || "");
      setEmail(data.user.email || "");
    } catch {}
  };

  useEffect(() => { reloadProfile(); }, []);

  useEffect(() => {
    API.get("/site-settings")
      .then(r => setDeleteWarning(r.data?.delete_account_warning || "This will permanently delete your account, all your coins, resources, workers, inventory, deposits, withdrawals history, and referral data. This action cannot be undone."))
      .catch(() => {});
  }, []);

  const handleDeleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== "DELETE") return toast.error("Type DELETE to confirm");
    setDeleting(true);
    try {
      await API.delete("/me", { data: { password: deletePw, confirm: deleteConfirm } });
      toast.success("Account deleted. Goodbye.");
      try { await logout(); } catch {}
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  const authMethod = profile?.user?.auth_method || "email";
  const fpLinked = profile?.user?.faucetpay_address;

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!currentPw) return toast.error("Current password is required to save changes");
    setSaving(true);
    try {
      const payload = { name, email, current_password: currentPw };
      if (newPw) payload.new_password = newPw;
      await API.put("/profile", payload);
      toast.success("Profile updated");
      setCurrentPw("");
      setNewPw("");
      checkAuth();
      reloadProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleLinkFaucetPay = async () => {
    if (!fpEmail.trim()) return toast.error("Enter your FaucetPay email");
    if (!fpPw) return toast.error("Current password is required");
    setLinkingFp(true);
    try {
      const { data } = await API.post("/profile/link-faucetpay", { email: fpEmail.trim(), current_password: fpPw });
      toast.success(data.message);
      setFpEmail("");
      setFpPw("");
      checkAuth();
      reloadProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to link FaucetPay"); }
    finally { setLinkingFp(false); }
  };

  const handleUnlinkFaucetPay = async () => {
    if (!unlinkPw) return toast.error("Current password is required");
    setUnlinking(true);
    try {
      const { data } = await API.post("/profile/unlink-faucetpay", { current_password: unlinkPw });
      toast.success(data.message);
      setUnlinkPw("");
      checkAuth();
      reloadProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to unlink FaucetPay"); }
    finally { setUnlinking(false); }
  };

  const handleSetPassword = async () => {
    if (!spEmail.trim() || !spPassword) return toast.error("Email and password are required");
    if (spPassword.length < 6) return toast.error("Password must be at least 6 characters");
    setSettingPassword(true);
    try {
      const { data } = await API.post("/profile/set-password", { email: spEmail.trim(), password: spPassword });
      toast.success(data.message);
      setSpEmail("");
      setSpPassword("");
      checkAuth();
      reloadProfile();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to set password"); }
    finally { setSettingPassword(false); }
  };

  const PasswordField = ({ value, onChange, show, onToggle, placeholder, testId }) => (
    <div className="relative mt-1">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder || "Enter password"}
        className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] focus:border-[#00F0FF] pr-10" data-testid={testId} />
      <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#94A3B8]">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <GameLayout title="Profile">
      <div className="space-y-6 max-w-2xl" data-testid="profile-page">
        {/* Profile Card */}
        <div className="bg-[#131722] rounded-2xl border border-[#2A3241]/40 overflow-hidden">
          <div className="h-32 relative">
            <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/fca54acd1290900d8e646c4b381fccc8b5383dbf475c7dba1e7ec6cc06116fbc.png"
              alt="" className="w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#131722]" />
          </div>
          <div className="px-6 pb-6 -mt-10 relative">
            <div className="w-20 h-20 rounded-full bg-[#1C212E] border-4 border-[#131722] flex items-center justify-center text-2xl font-bold text-[#00F0FF]">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <h2 className="text-xl font-bold text-[#F8FAFC] mt-3">{user?.name}</h2>
            <p className="text-sm text-[#64748B]">{profile?.user?.email}</p>
            <div className="flex flex-wrap gap-4 mt-3">
              <div>
                <p className="font-mono-game text-lg font-bold text-[#FFD600]">{user?.coins?.toFixed(0) || 0}</p>
                <p className="text-xs text-[#64748B]">Coins</p>
              </div>
              <div>
                <p className="font-mono-game text-lg font-bold text-[#10B981]">${user?.withdrawable_balance?.toFixed(2) || "0.00"}</p>
                <p className="text-xs text-[#64748B]">Balance</p>
              </div>
              <div>
                <p className="font-mono-game text-lg font-bold text-[#00F0FF]">{profile?.inventory_count || 0}</p>
                <p className="text-xs text-[#64748B]">Items</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold"
                style={{
                  borderColor: authMethod === "both" ? "#10B981" : authMethod === "faucetpay" ? "#FFD600" : "#00F0FF",
                  color: authMethod === "both" ? "#10B981" : authMethod === "faucetpay" ? "#FFD600" : "#00F0FF",
                  backgroundColor: authMethod === "both" ? "rgba(16,185,129,0.1)" : authMethod === "faucetpay" ? "rgba(255,214,0,0.1)" : "rgba(0,240,255,0.1)",
                }}
                data-testid="auth-method-badge">
                <ShieldCheck className="w-3.5 h-3.5" />
                {authMethod === "both" ? "Email + FaucetPay" : authMethod === "faucetpay" ? "FaucetPay Only" : "Email Only"}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile — for users who have a password (email or both) */}
        {authMethod !== "faucetpay" && (
          <form onSubmit={handleSaveProfile} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4" data-testid="profile-form">
            <h3 className="text-lg font-bold text-[#F8FAFC] flex items-center gap-2"><User className="w-5 h-5 text-[#00F0FF]" /> Edit Profile</h3>
            <div>
              <Label className="text-[#94A3B8] text-sm">Display Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="profile-name-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Email Address</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="profile-email-input" />
            </div>
            <div className="border-t border-[#2A3241]/40 pt-4 mt-2">
              <h4 className="text-sm font-bold text-[#94A3B8] flex items-center gap-1.5 mb-3"><Lock className="w-4 h-4" /> Change Password</h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-[#94A3B8] text-xs">Current Password <span className="text-[#EF4444]">*</span></Label>
                  <PasswordField value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} show={showCurrentPw}
                    onToggle={() => setShowCurrentPw(!showCurrentPw)} placeholder="Required to save any changes" testId="profile-current-pw" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">New Password <span className="text-[#64748B]">(leave blank to keep current)</span></Label>
                  <PasswordField value={newPw} onChange={(e) => setNewPw(e.target.value)} show={showNewPw}
                    onToggle={() => setShowNewPw(!showNewPw)} placeholder="Min 6 characters" testId="profile-new-pw" />
                </div>
              </div>
            </div>
            <button type="submit" disabled={saving} data-testid="profile-save-btn"
              className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        )}

        {/* Link FaucetPay — for email-only users */}
        {authMethod === "email" && (
          <div className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-6 space-y-4" data-testid="link-faucetpay-section">
            <h3 className="text-lg font-bold text-[#F8FAFC] flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#FFD600]" /> Link FaucetPay Wallet
            </h3>
            <p className="text-sm text-[#94A3B8]">
              Link your FaucetPay account to enable lower-fee withdrawals directly to FaucetPay.
            </p>
            <div>
              <Label className="text-[#94A3B8] text-sm">FaucetPay Email</Label>
              <Input type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} placeholder="The email registered on your FaucetPay account"
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 focus:border-[#FFD600]" data-testid="link-fp-email-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs">Current Password <span className="text-[#EF4444]">*</span></Label>
              <PasswordField value={fpPw} onChange={(e) => setFpPw(e.target.value)} show={showFpPw}
                onToggle={() => setShowFpPw(!showFpPw)} placeholder="Required to link" testId="link-fp-password" />
            </div>
            <button type="button" onClick={handleLinkFaucetPay} disabled={linkingFp} data-testid="link-fp-submit-btn"
              className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50">
              <Wallet className="w-4 h-4" /> {linkingFp ? "Verifying..." : "Link FaucetPay"}
            </button>
          </div>
        )}

        {/* Set Email & Password — for FP-only users */}
        {authMethod === "faucetpay" && (
          <div className="bg-[#131722] rounded-xl border border-[#00F0FF]/20 p-6 space-y-4" data-testid="set-password-section">
            <h3 className="text-lg font-bold text-[#F8FAFC] flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#00F0FF]" /> Set Email & Password
            </h3>
            <p className="text-sm text-[#94A3B8]">
              Your account was created via FaucetPay. Set a real email and password to also log in with email.
            </p>
            <div>
              <Label className="text-[#94A3B8] text-sm">Email Address</Label>
              <Input type="email" value={spEmail} onChange={(e) => setSpEmail(e.target.value)} placeholder="your@email.com"
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 focus:border-[#00F0FF]" data-testid="set-email-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Password</Label>
              <PasswordField value={spPassword} onChange={(e) => setSpPassword(e.target.value)} show={showSpPw}
                onToggle={() => setShowSpPw(!showSpPw)} placeholder="Min 6 characters" testId="set-password-input" />
            </div>
            <button type="button" onClick={handleSetPassword} disabled={settingPassword} data-testid="set-password-submit-btn"
              className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50">
              <Mail className="w-4 h-4" /> {settingPassword ? "Setting up..." : "Set Email & Password"}
            </button>
          </div>
        )}

        {/* Account Linked + Unlink — for 'both' auth users */}
        {authMethod === "both" && (
          <div className="bg-[#131722] rounded-xl border border-[#10B981]/20 p-6 space-y-4" data-testid="linked-status-section">
            <h3 className="text-lg font-bold text-[#F8FAFC] flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-[#10B981]" /> Account Linked
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-[#00F0FF]" />
                <span className="text-[#94A3B8]">Email Login:</span>
                <span className="text-[#F8FAFC]">{profile?.user?.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Wallet className="w-4 h-4 text-[#FFD600]" />
                <span className="text-[#94A3B8]">FaucetPay:</span>
                <span className="text-[#F8FAFC] font-mono text-xs">{fpLinked}</span>
              </div>
            </div>
            <p className="text-xs text-[#64748B]">You can log in using either method.</p>

            {/* Unlink FaucetPay */}
            <div className="border-t border-[#2A3241]/40 pt-4 mt-2">
              <h4 className="text-sm font-bold text-[#EF4444]/80 flex items-center gap-1.5 mb-3"><Unlink className="w-4 h-4" /> Unlink FaucetPay</h4>
              <p className="text-xs text-[#94A3B8] mb-3">This will remove FaucetPay access. You'll only be able to log in with email and password.</p>
              <div>
                <Label className="text-[#94A3B8] text-xs">Current Password <span className="text-[#EF4444]">*</span></Label>
                <PasswordField value={unlinkPw} onChange={(e) => setUnlinkPw(e.target.value)} show={showUnlinkPw}
                  onToggle={() => setShowUnlinkPw(!showUnlinkPw)} placeholder="Required to unlink" testId="unlink-fp-password" />
              </div>
              <button type="button" onClick={handleUnlinkFaucetPay} disabled={unlinking} data-testid="unlink-fp-submit-btn"
                className="flex items-center gap-2 mt-3 px-5 py-2.5 text-xs font-bold uppercase tracking-widest border border-[#EF4444]/40 text-[#EF4444] rounded-lg hover:bg-[#EF4444]/10 disabled:opacity-50">
                <Unlink className="w-3.5 h-3.5" /> {unlinking ? "Unlinking..." : "Unlink FaucetPay"}
              </button>
            </div>
          </div>
        )}

        {/* Danger Zone: Delete Account */}
        {user?.role !== "admin" && (
          <div className="bg-[#131722] rounded-2xl border border-[#EF4444]/30 p-6" data-testid="danger-zone">
            <div className="flex items-start gap-3 mb-3">
              <Trash2 className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-bold text-[#EF4444]">Danger Zone</h3>
                <p className="text-xs text-[#94A3B8] mt-0.5">Permanent and irreversible actions.</p>
              </div>
            </div>
            <p className="text-sm text-[#94A3B8] mb-4 whitespace-pre-wrap">{deleteWarning}</p>
            <button type="button" onClick={() => { setShowDeleteModal(true); setDeletePw(""); setDeleteConfirm(""); }} data-testid="open-delete-account-btn"
              className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-widest border border-[#EF4444]/40 text-[#EF4444] rounded-lg hover:bg-[#EF4444]/10">
              <Trash2 className="w-3.5 h-3.5" /> Delete My Account
            </button>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !deleting && setShowDeleteModal(false)} data-testid="delete-account-modal">
          <div className="bg-[#131722] border border-[#EF4444]/40 rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#EF4444]/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-[#EF4444]" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#EF4444]">Delete Account</h3>
                <p className="text-xs text-[#94A3B8]">This cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-[#F8FAFC] bg-[#0B0E14] border border-[#EF4444]/20 rounded-lg p-3 mb-4 whitespace-pre-wrap">{deleteWarning}</p>
            {profile?.user?.auth_method !== "faucetpay" && (
              <div className="mb-3">
                <Label className="text-[#94A3B8] text-xs">Current password</Label>
                <Input type="password" value={deletePw} onChange={(e) => setDeletePw(e.target.value)} placeholder="Your password"
                  className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="delete-password-input" />
              </div>
            )}
            <div className="mb-4">
              <Label className="text-[#94A3B8] text-xs">Type <span className="font-mono font-bold text-[#EF4444]">DELETE</span> to confirm</Label>
              <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE"
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 font-mono tracking-widest" data-testid="delete-confirm-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowDeleteModal(false)} disabled={deleting} data-testid="delete-cancel-btn"
                className="flex-1 py-2.5 text-xs font-bold uppercase tracking-widest border border-[#2A3241] text-[#94A3B8] rounded-lg hover:bg-[#1C212E]">
                Cancel
              </button>
              <button onClick={handleDeleteAccount} disabled={deleting || deleteConfirm.trim().toUpperCase() !== "DELETE"} data-testid="confirm-delete-btn"
                className="flex-1 py-2.5 text-xs font-bold uppercase tracking-widest bg-[#EF4444] text-white rounded-lg hover:bg-[#EF4444]/90 disabled:opacity-50">
                {deleting ? "Deleting..." : "Delete Forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </GameLayout>
  );
}
